import { Button, DataTable, FlexLayout, Icon } from '@deity-io/portal-ui';
import { BaseHelmet, ConfirmationModal, CreateEditFlowModal } from 'components';
import { ValidationStatusBadge } from 'components/Flows/ValidationStatusBadge';
import { APOLLO_BASE_OPTIONS, ENTITY_PATH, ENTITY_PATH_ID, LOCALE_DATE_OPTIONS } from 'contracts/constants';
import { DEFAULT_PAGINATION_SIZE } from 'contracts/constants/page-title';
import { EntityWrapper } from 'layouts';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  GetFlowsByPackageVersionDocument,
  useDeleteFlowMutation,
  useGetFlowsByPackageVersionLazyQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { v4 } from 'uuid';

interface FlowsProps {
  isEditable: boolean;
}
const Flows: FC<FlowsProps> = ({ isEditable }) => {
  const history = useHistory();
  const [paging, setPaging] = useState({ page: 1, pageSize: DEFAULT_PAGINATION_SIZE });
  const { url } = useRouteMatch();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState<any>();

  const { pathname } = useLocation();
  const [getAllFlows, { loading, data: flows, fetchMore }] = useGetFlowsByPackageVersionLazyQuery(APOLLO_BASE_OPTIONS);
  const params: any = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const totalItems = flows?.getFlowsByPackageVersion?.totalItems;
  const data = flows?.getFlowsByPackageVersion?.data;
  const [newFlowKey, setNewFlowKey] = useState(v4());

  const [deleteFlow] = useDeleteFlowMutation({
    refetchQueries: [
      {
        query: GetFlowsByPackageVersionDocument,
        variables: {
          packageVersionUuid: params?.versionUuid,
          offset: 0,
          limit: data?.length || paging.pageSize
        }
      }
    ]
  });

  useEffect(() => {
    if (params?.versionUuid) {
      getAllFlows({
        variables: {
          packageVersionUuid: params.versionUuid,
          offset: (paging.page - 1) * paging.pageSize,
          limit: paging.pageSize
        }
      });
    }
  }, [pathname, paging]);

  // eslint-disable-next-line react/no-unstable-nested-components
  const NewFlowButton = () => (
    <Button
      variant="outlined"
      disabled={isModalOpen}
      onClick={() => setIsModalOpen(true)}
      startIcon={<Icon icon="add" />}
    >
      New Flow
    </Button>
  );

  return (
    <>
      <BaseHelmet page="Flows" />

      <EntityWrapper title="Flows" button={<NewFlowButton />}>
        <DataTable
          data={data || []}
          columns={[
            {
              key: 'name',
              name: 'FLOW',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: ({ value }) => <b>{value}</b>,
              width: 40
            },
            {
              key: 'flowType',
              name: 'TYPE'
              // formatter: ({ value }) => <b>{value}</b>
            },
            {
              key: 'validation',
              name: 'STATUS',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: validation => <ValidationStatusBadge validation={validation.value} />
            },
            {
              key: 'modifiedAt',
              name: 'LAST MODIFIED',
              formatter: ({ value }) => new Date(value!).toLocaleString('nl', LOCALE_DATE_OPTIONS)
            },
            {
              key: 'modifiedBy',
              name: 'LAST MODIFIED BY',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: () => <div>John Smith</div>
            },
            {
              key: 'uuid',
              name: 'ACTIONS',
              isActionCell: true,
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: ({ value }) => (
                <FlexLayout>
                  <Button endIcon={<Icon icon="visibility" />} onClick={() => history.push(`${url}/${value}`)} />

                  {isEditable && (
                    <Button
                      endIcon={<Icon icon="delete" color="error" />}
                      onClick={() => {
                        setFlowToDelete(value);
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  )}
                </FlexLayout>
              )
            }
          ]}
          pageSize={paging.pageSize}
          onPageSizeChange={size => {
            setPaging({ ...paging, pageSize: size });
          }}
          totalResults={totalItems || 0}
          page={paging.page}
          showPagination
          onPageChange={pageNr => {
            setPaging({ ...paging, page: pageNr });
          }}
          onRowClick={endpoint => history.push(`${url}/${endpoint.uuid}`)}
          fullClickable
          isLoading={loading}
          noResultsMsg="No flows available for this version"
        />
      </EntityWrapper>

      <CreateEditFlowModal
        key={newFlowKey}
        isOpen={isModalOpen}
        versionUuid={params?.versionUuid}
        setOpenState={state => setIsModalOpen(state)}
        onPackageCreated={() => {
          setIsModalOpen(false);
          setNewFlowKey(v4());
          setPaging({ ...paging, page: 1 });
        }}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        entityType="flow"
        entityUuid={flowToDelete}
        onConfirm={uuid => deleteFlow({ variables: { packageVersionUuid: params.versionUuid, uuid: uuid! } })}
      />
    </>
  );
};

export default Flows;
